import * as React from 'react';
import { useContext } from 'react';

import type { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';

/**
 * Context for org-scoped enterprise modals.
 * Currently supports:
 * - ENTERPRISE_TERMS_OF_USE
 * - ADD_EMAIL (OrgScopedForcedSSORecoveryEmailModal)
 *
 * TODO: Add additional modal types as they are migrated to org-scoped:
 * - PROGRAM_ACCESS (program-scoped - needs program membership data)
 * - LOGIN (program-scoped - needs program membership data)
 * - AUTOENROLLED_COURSES (program-scoped - needs program membership data)
 * - PROGRAM_JOIN_ERROR (program-scoped - needs program membership data)
 */

export type OrgScopedModalName = (typeof ENTERPRISE_MODAL_NAMES)[keyof typeof ENTERPRISE_MODAL_NAMES];

export type EnterpriseOrgScopedModalManagerContextType = {
  modalToShow: OrgScopedModalName | null;
  isLoading: boolean;

  // EnterpriseTermsOfUseModal state
  showEnterpriseTermsOfUseModal: boolean;
  setShowEnterpriseTermsOfUseModal: React.Dispatch<React.SetStateAction<boolean>>;
  isEnterpriseAdmin: boolean;
  shouldShowCCPAMarketingConsent: boolean;
  shouldShowGDPRMarketingConsent: boolean;
  shouldShowThirdPartyTermsOfConsent: boolean;

  // ChangePreviewProgramsModal state (user-triggered, not auto-shown)
  showChangePreviewProgramsModal: boolean;
  setShowChangePreviewProgramsModal: React.Dispatch<React.SetStateAction<boolean>>;

  // Org-scoped auto-join state: set by useOrgJoinAllPendingMutation, consumed by banners
  orgAutoJoinErrorCode: string | null;
  setOrgAutoJoinErrorCode: React.Dispatch<React.SetStateAction<string | null>>;
  orgAutoJoinLoading: boolean;
  setOrgAutoJoinLoading: React.Dispatch<React.SetStateAction<boolean>>;

  // TODO: Add program-scoped modal state when migrating those modals:
  // - isAccessModalDismissed / setIsAccessModalDismissed (for ProgramAccessModal)
  // - isAutoenrolledCoursesModalDismissed / setIsAutoenrolledCoursesModalDismissed (for AutoenrolledCoursesModal)
  // - setHasShownLoginModal (for login redirect)
  // - programJoinErrorInfo / setProgramJoinErrorInfo (for ProgramJoinErrorModal)
};

const EnterpriseOrgScopedModalManagerContext = React.createContext<
  EnterpriseOrgScopedModalManagerContextType | undefined
>(undefined);

/**
 * Hook to access org-scoped enterprise modal states.
 * Currently supports EnterpriseTermsOfUseModal and ADD_EMAIL modals.
 */
export const useEnterpriseOrgScopedModalManager = () => {
  const modalManager = useContext(EnterpriseOrgScopedModalManagerContext);
  return modalManager;
};

export default EnterpriseOrgScopedModalManagerContext;
